<template>
    <!-- Decorativo: o estado da conexão também está no selo do cabeçalho e no texto da tela. -->
    <div class="ca-diagram" :class="[`is-${state}`, { 'has-failed-test': failed }]" aria-hidden="true">
        <span class="ca-party">
            <BrandTileCmp :size="64" :image="muvenMarkUrl" background="var(--brand-fill)" color="var(--text-on-brand)" />
            <span class="ca-party-name">Muven</span>
        </span>

        <span class="ca-wire">
            <span class="ca-node">
                <fa icon="exclamation-triangle" v-if="failed" />
                <fa icon="check" v-else-if="state === 'connected'" />
                <fa icon="sync-alt" spin v-else-if="state === 'connecting'" />
                <fa :icon="idleIcon" v-else />
            </span>
        </span>

        <span class="ca-party">
            <BrandTileCmp :size="64" :image="channelMarkUrl" :icon="isCustom ? 'store-alt' : null"
                :text="brand.monogram" :background="brand.color" :color="brand.textColor" />
            <span class="ca-party-name">{{realName}}</span>
        </span>
    </div>
</template>

<script>
import { ChannelEnum } from '@c2b/muven-commons'
import { getChannelBrand } from '@/channel/models/ChannelBrand'
import BrandTileCmp from '@/utils/components/BrandTileCmp.vue'

/**
 * Muven ⇄ canal. O fio conta o estado: tracejado (sem conexão), pontos correndo
 * (conectando/testando), contínuo verde (conectado) e vermelho (último teste falhou).
 */
export default {
    name: 'ChannelConnectionDiagramCmp',
    components: {
        BrandTileCmp,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
        // 'idle' | 'connecting' | 'connected'
        state: {
            type: String,
            default: 'idle',
        },
        failed: {
            type: Boolean,
            default: false,
        },
        idleIcon: {
            type: String,
            default: 'link',
        },
    },
    computed: {
        brand() {
            return getChannelBrand(this.channel.enumChannel)
        },
        isCustom() {
            return this.channel.enumChannel === ChannelEnum.Custom
        },
        realName() {
            return this.channel.getRealName ? this.channel.getRealName() : this.channel.name
        },
        channelMarkUrl() {
            return this.brand.mark ? require(`@/channel/images/marks/${this.brand.mark}`) : null
        },
        muvenMarkUrl() {
            return require('@/channel/images/marks/muven-cart.png')
        },
    },
}
</script>

<style lang="scss" scoped>
$ease-out: cubic-bezier(.22, 1, .36, 1);

.ca-diagram {
    display: grid;
    grid-template-columns: auto minmax(80px, 260px) auto;
    align-items: center;
    justify-content: center;
    gap: 0 16px;
}

.ca-party {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 96px;
}

.ca-party-name {
    max-width: 100%;
    overflow: hidden;
    color: var(--text-soft);
    font-size: 13px;
    font-weight: 700;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ca-wire {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 64px;
    margin-bottom: 26px;

    // O fio: tracejado quando não conectado, contínuo quando conectado
    &::before {
        content: '';
        position: absolute;
        top: 50%;
        right: 0;
        left: 0;
        border-top: 2px dashed var(--border-strong);
        transform: translateY(-50%);
    }
}

.ca-node {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 2px solid var(--border-strong);
    border-radius: 50%;
    background: var(--surface-1);
    color: var(--text-faint);
    font-size: 15px;
}

.ca-diagram.is-connecting {
    .ca-wire::before {
        height: 2px;
        border-top: none;
        // Pontos correndo do Muven até o canal
        background-image: radial-gradient(circle, var(--brand) 1.5px, transparent 2px);
        background-size: 12px 2px;
        animation: ca-flow .8s linear infinite;
    }

    .ca-node {
        border-color: var(--tint-brand-border);
        background: var(--tint-brand-bg);
        color: var(--brand);
    }
}

.ca-diagram.is-connected {
    .ca-wire::before {
        border-top-style: solid;
        border-top-color: var(--success-border);
    }

    .ca-node {
        border-color: var(--success-border);
        background: var(--success-bg);
        color: var(--success-fg);
        animation: ca-pop .5s $ease-out both;
    }
}

// Último teste falhou: o fio deixa de dizer "tudo certo"
.ca-diagram.has-failed-test {
    .ca-wire::before {
        height: auto;
        background: none;
        border-top: 2px dashed var(--danger-border);
        animation: none;
    }

    .ca-node {
        border-color: var(--danger-border);
        background: var(--danger-bg);
        color: var(--danger-fg);
        animation: ca-pop .5s $ease-out both;
    }
}

@keyframes ca-flow {
    from { background-position: 0 0; }
    to   { background-position: 12px 0; }
}

@keyframes ca-pop {
    0%   { transform: scale(.6); }
    100% { transform: scale(1); }
}

@media (max-width: 576px) {
    .ca-diagram {
        grid-template-columns: auto minmax(40px, 1fr) auto;
        gap: 0 8px;
    }

    .ca-party {
        width: 72px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .ca-diagram .ca-wire::before,
    .ca-diagram .ca-node {
        animation: none !important;
    }
}
</style>
